// Zugang zu den Balancing-Tabellen.
//
// **Das Frontend haelt keine eigenen Zahlen** (Gate FG-31). Es laedt sie beim
// Start vom Server; ist keiner erreichbar, benutzt es den mitgelieferten
// Schnappschuss `contracts/balancing.json` — dieselbe Datei, vom Server
// erzeugt und von einem Backend-Gate aktuell gehalten.
//
// Der Schnappschuss ist damit **kein zweiter Satz Zahlen**, sondern ein
// Abzug des ersten. Genau deshalb ist der Offline-Modus erlaubt.

import snapshot from '../../../contracts/balancing.json';
import type {
  AnomalyKind,
  Balancing,
  Cache,
  Gear,
  Booster,
  Build,
  Chapter,
  ColonyBuilding,
  ColonyRecipe,
  Enemy,
  EraTable,
  FleetOrder,
  Good,
  Perk,
  Planet,
  ProbeLog,
  Project,
  Quest,
  Recipe,
  Resource,
  RockKind,
  SideQuest,
  StatCounter,
  TreeNode,
  Zone,
} from './types';

let current: Balancing = snapshot as unknown as Balancing;
let fromServer = false;

/** Der eingebaute Abzug — Startwert und Offline-Rueckfall. */
export const OFFLINE_BALANCING = snapshot as unknown as Balancing;

export function balancing(): Balancing {
  return current;
}

export function isFromServer(): boolean {
  return fromServer;
}

/** Die vom Server geladenen Tabellen uebernehmen. */
export function adoptBalancing(data: Balancing): void {
  current = data;
  fromServer = true;
  rebuildIndexes();
}

// ── Nachschlagekarten ────────────────────────────────────────────────────────
// Lesezugriff laeuft nie ueber eine lineare Suche — dieselbe Regel wie im
// Backend, wo neben jeder Tabelle eine `*_BY_ID`-Karte steht.

let nodeById = new Map<string, TreeNode>();
let buildById = new Map<string, Build>();
let questById = new Map<string, Quest>();
let sideQuestById = new Map<string, SideQuest>();
let boosterById = new Map<string, Booster>();
let resourceById = new Map<string, Resource>();
let counterById = new Map<string, StatCounter>();
let enemyById = new Map<string, Enemy>();
let perkById = new Map<string, Perk>();
let recipeById = new Map<string, Recipe>();
let anomalyById = new Map<string, AnomalyKind>();
let probeLogById = new Map<string, ProbeLog>();
let fleetOrderById = new Map<string, FleetOrder>();
let cacheById_ = new Map<string, Cache>();
let cacheIndexById = new Map<string, number>();
let gearById_ = new Map<string, Gear>();
let gearIndexById = new Map<string, number>();
let rockKindById = new Map<string, RockKind>();
let eraById = new Map<number, EraTable>();
let goodById_ = new Map<string, Good>();
let colonyRecipeById_ = new Map<string, ColonyRecipe>();
let colonyBuildingById_ = new Map<string, ColonyBuilding>();
let planetById_ = new Map<string, Planet>();
let planetIndexById = new Map<string, number>();
let projectById_ = new Map<string, Project>();
let projectsByPlanet = new Map<string, Project[]>();

function rebuildIndexes(): void {
  nodeById = new Map(current.nodes.map((node) => [node.id, node]));
  buildById = new Map(current.builds.map((build) => [build.id, build]));
  questById = new Map(current.quests.map((quest) => [quest.id, quest]));
  sideQuestById = new Map(current.sideQuests.map((entry) => [entry.id, entry]));
  boosterById = new Map(current.boosters.map((booster) => [booster.id, booster]));
  resourceById = new Map(current.resources.map((resource) => [resource.id, resource]));
  counterById = new Map(current.statCounters.map((counter) => [counter.id, counter]));
  enemyById = new Map(current.enemies.map((enemy) => [enemy.id, enemy]));
  perkById = new Map(current.perks.map((entry) => [entry.id, entry]));
  recipeById = new Map(current.recipes.map((entry) => [entry.id, entry]));
  anomalyById = new Map(current.anomalies.map((entry) => [entry.id, entry]));
  probeLogById = new Map(current.probeLogs.map((entry) => [entry.id, entry]));
  fleetOrderById = new Map(current.fleetOrders.map((entry) => [entry.id, entry]));
  cacheById_ = new Map(current.caches.map((entry) => [entry.id, entry]));
  cacheIndexById = new Map(current.caches.map((entry, index) => [entry.id, index]));
  gearById_ = new Map(current.gear.map((entry) => [entry.id, entry]));
  gearIndexById = new Map(current.gear.map((entry, index) => [entry.id, index]));
  // Der glaenzende Brocken steht neben der Klassenliste, nicht darin — die Karte
  // fuehrt beide, wie `ROCK_KIND_BY_ID` im Backend.
  rockKindById = new Map(
    [...current.rockKinds, current.richKind].map((kind) => [kind.id, kind]),
  );
  eraById = new Map(current.eras.map((entry) => [entry.era, entry]));
  goodById_ = new Map(current.goods.map((entry) => [entry.id, entry]));
  colonyRecipeById_ = new Map(current.colonyRecipes.map((entry) => [entry.id, entry]));
  colonyBuildingById_ = new Map(current.colonyBuildings.map((entry) => [entry.id, entry]));
  planetById_ = new Map(current.planets.map((entry) => [entry.id, entry]));
  planetIndexById = new Map(current.planets.map((entry, index) => [entry.id, index]));
  projectById_ = new Map(current.projects.map((entry) => [entry.id, entry]));
  projectsByPlanet = new Map(
    current.planets.map((planet) => [
      planet.id, current.projects.filter((entry) => entry.planet === planet.id),
    ]),
  );
}

rebuildIndexes();

export const nodes = (): TreeNode[] => current.nodes;
export const builds = (): Build[] => current.builds;
export const quests = (): Quest[] => current.quests;
export const sideQuests = (): SideQuest[] => current.sideQuests;
export const boosters = (): Booster[] => current.boosters;
export const resources = (): Resource[] => current.resources;
export const eras = (): EraTable[] => current.eras;
export const statCounters = (): StatCounter[] => current.statCounters;
export const enemies = (): Enemy[] => current.enemies;
export const story = (): Chapter[] => current.story;
export const perks = (): Perk[] => current.perks;
export const recipes = (): Recipe[] => current.recipes;
export const anomalies = (): AnomalyKind[] => current.anomalies;
export const probeLogs = (): ProbeLog[] => current.probeLogs;
export const fleetOrders = (): FleetOrder[] => current.fleetOrders;

export const node = (id: string): TreeNode | undefined => nodeById.get(id);
export const build = (id: string): Build | undefined => buildById.get(id);
export const quest = (id: string): Quest | undefined => questById.get(id);
export const sideQuest = (id: string): SideQuest | undefined => sideQuestById.get(id);

/** Die Tabellenposition eines Nebenauftrags — sie geht in die Saat seiner Orte ein. */
export const sideQuestIndex = (id: string): number =>
  current.sideQuests.findIndex((entry) => entry.id === id);
export const booster = (id: string): Booster | undefined => boosterById.get(id);
export const resource = (id: string): Resource | undefined => resourceById.get(id);
export const statCounter = (id: string): StatCounter | undefined => counterById.get(id);
export const enemy = (id: string): Enemy | undefined => enemyById.get(id);
export const perk = (id: string): Perk | undefined => perkById.get(id);
export const recipe = (id: string): Recipe | undefined => recipeById.get(id);
export const anomalyKind = (id: string): AnomalyKind | undefined => anomalyById.get(id);
export const probeLog = (id: string): ProbeLog | undefined => probeLogById.get(id);
export const fleetOrder = (id: string): FleetOrder | undefined => fleetOrderById.get(id);
export const rockKind = (id: string): RockKind | undefined => rockKindById.get(id);
export const cacheById = (id: string): Cache | undefined => cacheById_.get(id);
export const gearById = (id: string): Gear | undefined => gearById_.get(id);
export const gearIndexOf = (id: string): number => gearIndexById.get(id) ?? -1;

// ── Akt III ──────────────────────────────────────────────────────────────────
export const goods = (): Good[] => current.goods;
export const planets = (): Planet[] => current.planets;
export const projects = (): Project[] => current.projects;
export const colonyBuildings = (): ColonyBuilding[] => current.colonyBuildings;
export const colonyRecipes = (): ColonyRecipe[] => current.colonyRecipes;
export const goodById = (id: string): Good | undefined => goodById_.get(id);
export const colonyRecipeById = (id: string): ColonyRecipe | undefined => colonyRecipeById_.get(id);
export const colonyBuildingById = (id: string): ColonyBuilding | undefined =>
  colonyBuildingById_.get(id);
export const planetById = (id: string): Planet | undefined => planetById_.get(id);
/** Die Tabellenposition eines Planeten — sie geht in die Saat von Ort und Oberfläche ein. */
export const planetIndexOf = (id: string): number => planetIndexById.get(id) ?? -1;
export const projectById = (id: string): Project | undefined => projectById_.get(id);
/** Die Projekte eines Planeten, in Tabellenordnung — sie sind streng sequenziell. */
export const projectsOf = (planetId: string): Project[] => projectsByPlanet.get(planetId) ?? [];

/** Die Tabellenposition einer Fundstelle — sie geht in die Saat ihres Ortes ein. */
export const cacheIndexOf = (id: string): number => cacheIndexById.get(id) ?? -1;

export const buildOrder = (): string[] => current.builds.map((entry) => entry.id);

/**
 * Die Zonentabelle einer Ära. Unbekannt fällt auf Akt I zurück.
 *
 * **Wortgleiche Entsprechung von `T.zones_for`.** Die Ära ist überall ein
 * Parameter und nirgends ein Modulzustand: `data/` liegt unter `state/`
 * (Gate FG-04) und darf den Spielstand nicht kennen — und ein zweites
 * mutables Singleton neben dem Spielstand wäre genau die zweite Wahrheit,
 * gegen die `zustand.md` geschrieben ist.
 */
export function zonesFor(era: number = current.eraFirst): Zone[] {
  // `current.eras[0]` ist Akt I: der Abzug schreibt die Ären in
  // Tabellenreihenfolge, und die fängt bei Akt I an. Ein zweiter Rückfall
  // („und wenn auch das fehlt, dann leer") wäre ein Zweig, den kein Test je
  // erreicht — und ein leeres Zonenfeld hieße ein Feld ohne Erz.
  return (eraById.get(era) ?? current.eras[0]).zones;
}

/**
 * Die Zonen einer Ära, in denen ein bestimmtes Erz fällt — in Tabellenordnung.
 *
 * „Wo finde ich das?" ist die häufigste Frage an eine Erzleiste, und die
 * Antwort stand bisher nur einmal, **inline** im Hilfetext. Ein zweiter
 * Aufrufer hätte sie kopiert; sie steht deshalb hier, wo die Zonentabelle
 * ohnehin liegt.
 *
 * Leer ist eine gültige Antwort und keine Panne: was der Konverter erzeugt,
 * liegt in **keiner** Zonentabelle — eine Ressource im Feld wäre eine
 * zusätzliche Erzziehung, und jede folgende verschöbe sich (Gate FG-30).
 */
export function zonesOfOre(id: string, era: number = current.eraFirst): Zone[] {
  return zonesFor(era).filter((zone) => zone.ores.some((ore) => ore.res === id));
}

/** Das Salz einer Ära, oder `null` für Akt I. Siehe `eraSeed` in `sim/field.ts`. */
export function eraSalt(era: number): number | null {
  return eraById.get(era)?.salt ?? null;
}

/**
 * Die äußerste Entfernung, die ein Schiff erreicht — **die eine Lesung der
 * Weltgrenze**.
 *
 * Sie steht hier und nicht dreimal verstreut, weil vier Dinge daran hängen, die
 * nichts miteinander zu tun haben: der Kartenmaßstab, die Suchweite der
 * Peilung, die Wandanzeige und die Tiefenziele der Aufträge. Jede eigene
 * Fassung von `fieldRadius + wallBand` wäre eine Kopie, die still veraltet —
 * und zwar in die schlimmste Richtung: die Karte zeigte einen Rand, an dem
 * keiner ist, oder die Peilung durchsuchte eine Fläche, die niemand erreicht.
 *
 * Der äußere Rand des Wandbandes ist der richtige Bezugspunkt, nicht
 * `fieldRadius` selbst: bis dorthin **kann** man fliegen, der Rückschub wächst
 * nur an. Jenseits davon rechnet die Welt weiter (`WORLD-001`) — sie ist nur
 * nicht mehr erreichbar.
 */
export function worldLimit(): number {
  return current.world.fieldRadius + current.world.wallBand;
}

/** Index der Zone an einer Weltposition (Abstand zur Basis im Ursprung). */
export function zoneAtDist(d: number, era: number = current.eraFirst): number {
  const list = zonesFor(era);
  let index = 0;
  for (let i = 0; i < list.length; i++) if (d >= list[i].from) index = i;
  return index;
}

/** XP-Bedarf fuer das naechste Schiffslevel. */
export const xpForLevel = (level: number): number => Math.floor(100 * Math.pow(1.75, level - 1));
